import {Inject, Injectable} from '@angular/core';
import {ComponentStore} from '@ngrx/component-store';
import {switchMap, tap, withLatestFrom} from 'rxjs/operators';
import {EMPTY, Observable} from 'rxjs';
import {HttpErrorResponse} from "@angular/common/http";
import firebase from "firebase/compat/app";
import {onValue, ref} from "firebase/database";
import Unsubscribe = firebase.Unsubscribe;
import * as _ from 'lodash';
import {BranchModel} from "../../models/branch.model";
import {BranchesHttpService} from "../services/branches-http.service";
import {BranchesQueryModel} from "../models/branches-query.model";
import {BranchesFilterModel} from "../models/branches-filter.model";

export interface BranchesComponentState {
  branches: BranchModel[];
  cursor?: string;
  loading: boolean;
  error: string | null;
}

@Injectable()
export class BranchesComponentStore extends ComponentStore<BranchesComponentState> {

  // Selectors
  readonly branches$ = this.select((state) => state.branches);
  readonly loading$ = this.select((state) => state.loading);
  readonly error$ = this.select((state) => state.error);
  readonly cursor$ = this.select((state) => state.cursor);

  vm$ = this.select({
    branches: this.branches$,
    loading: this.loading$,
    error: this.error$,
    cursor: this.cursor$
  })


  private channelsUnsubscribeMap: Map<string, Unsubscribe> = new Map();

  constructor(
    @Inject(BranchesHttpService) private branchesHttpService: BranchesHttpService,
    @Inject('firebaseService') private firebaseService
  ) {
    super({
      branches: [],
      cursor: '',
      loading: false,
      error: null,
    });
  }

  // Updaters
  readonly onSetBranches = this.updater((state: BranchesComponentState, value: {
    branches: BranchModel[],
    cursor?: string
  }) => ({
    ...state,
    branches: _.uniq([...state.branches, ...value.branches]),
    cursor: value.cursor,
    loading: false,
    error: null,
  }));

  readonly updateBranch = this.updater((state: BranchesComponentState, updatedBranch: BranchModel) => ({
    ...state,
    branches: state.branches.map((branch: BranchModel) => {
        return updatedBranch.id === branch.id ? {...branch, ...updatedBranch} : branch;
      }
    ),
  }));

  // Updaters
  readonly onResetBranches = this.updater((state: BranchesComponentState) => ({
    ...state,
    branches: [],
    cursor: null,
    loading: false,
    error: null,
  }));

  readonly setLoading = this.updater((state, loading: boolean) => ({
    ...state,
    loading,
  }));

  readonly setError = this.updater((state, error: HttpErrorResponse) => ({
    ...state,
    error: error.message,
    loading: false,
  }));

  // Effect for fetching branches
  readonly getBranches = this.effect(
    (trigger$: Observable<BranchesQueryModel>) =>
      trigger$.pipe(
        tap(() => this.setLoading(true)), // Set loading state
        withLatestFrom(this.cursor$, this.branches$), // Combine with cursor from the state
        switchMap(([params, cursor, branches]) => {
            if (params.ids?.length) {

              const branchIds = branches.map(a => a.id);
              // Find missing IDs (those NOT in the store)
              const idsToRequest = params.ids.filter(id => !branchIds.includes(id));

              if (idsToRequest.length === 0) {
                // All requested IDs exist in the store, SKIP API call
                this.setLoading(false);
                return EMPTY;
              }

              // Update params to fetch only missing IDs
              params = {...params, ids: idsToRequest};
            }
            const filter: BranchesFilterModel = _.omitBy({
              ids: params.ids,
              query: params.query
            }, _.isNil);
            const requestParams = _.omitBy({
              limit: params.limit,
              sort: params.sort,
              cursor: cursor || ''
            }, _.isNil);
            return this.branchesHttpService.query(filter, requestParams).pipe(
              tap({
                next: (response) => {

                  //Subscribe to branches
                  response.list?.forEach((branch: BranchModel) => {
                    this.subscribe(branch.id);
                  })
                  return this.onSetBranches({branches: response.list, cursor: response.cursor});
                },
                error: (err) => this.setError(err.message || 'Failed to fetch branches'),
                finalize: () => this.setLoading(false)
              })
            )
          }
        )
      )
  );

  readonly removeBranches = this.updater(
    (state, ids: string[]) => ({
      ...state,
      branches: state.branches.filter((branch) => !ids.includes(branch.id))
    })
  );


  // Reset
  readonly resetBranches = this.effect<void>((trigger$) =>
    trigger$.pipe(
      tap(() => {
        this.unSubscribeAll();
        this.onResetBranches();
      })
    )
  );

  private unSubscribeAll(): void {
    for (const [id, channelUnsubscribe] of this.channelsUnsubscribeMap) {
      channelUnsubscribe();
    }
    this.channelsUnsubscribeMap.clear();
  }

  // Convert Firebase `onValue` into an observable
  public subscribe(id: string) {
    const channel = this.firebaseService.channel;
    const database = this.firebaseService.database;

    const path = `${channel.channelId}/${'branches_changed'}/${id}`;
    const dbRef = ref(database, path);

    // Listen for real-time updates
    const unsubscribe = onValue(
      dbRef,
      (snapshot) => {
        const branch: BranchModel = snapshot.val();
        if (!!branch) {
          this.updateBranch(branch);
        }
      }
    );

    // Cleanup when subscription is closed
    this.channelsUnsubscribeMap.set(id, unsubscribe);
  }

}
